import type { PostingReadinessIssue } from '../../../shared/posting-readiness';

import type { QualityCheck, QualityStatus, QualityVerdict } from '@/lib/content-quality';

export interface ReconciledCheck {
  id: string;
  label: string;
  status: QualityStatus;
  detail: string;
  fix?: QualityCheck['fix'];
}

export interface ReconciledVerdict {
  checks: ReconciledCheck[];
  passCount: number;
  warnCount: number;
  failCount: number;
}

const DIMENSION_LABEL: Record<string, string> = {
  connection: 'Account',
  caption: 'Caption',
  media: 'Media',
  schedule: 'Schedule',
  lifecycle: 'Status',
  transform: 'Media format',
  governance: 'Approval',
};

const COVERED_BY: Record<string, string> = {
  caption_length: 'caption',
  format: 'media',
  aspect: 'media',
  media_kind: 'media',
};

export function reconcileVerdictWithReadiness(
  verdict: Pick<QualityVerdict, 'checks'>,
  issues: readonly PostingReadinessIssue[],
): ReconciledVerdict {
  const issueRows: ReconciledCheck[] = issues.map((issue) => ({
    id: `readiness-${issue.code}`,
    label: DIMENSION_LABEL[issue.dimension] ?? 'Readiness',
    status: issue.severity === 'blocked' ? 'fail' : 'warn',
    detail: issue.message,
  }));

  const carriedFixes = new Map<string, QualityCheck['fix']>();
  const kept: ReconciledCheck[] = [];
  for (const check of verdict.checks) {
    const covering = COVERED_BY[check.id];
    const isProblem = check.status === 'fail' || check.status === 'warn';
    const duplicated =
      isProblem &&
      covering !== undefined &&
      issues.some(
        (issue) =>
          issue.dimension === covering && (check.status !== 'fail' || issue.severity === 'blocked'),
      );
    if (duplicated) {
      if (check.fix && covering) carriedFixes.set(covering, check.fix);
      continue;
    }
    kept.push({
      id: check.id,
      label: check.label,
      status: check.status,
      detail: check.detail,
      fix: check.fix,
    });
  }

  const usedDimensions = new Set<string>();
  const rows = issueRows.map((row, index) => {
    const dimension = issues[index].dimension;
    if (usedDimensions.has(dimension)) return row;
    const fix = carriedFixes.get(dimension);
    if (!fix) return row;
    usedDimensions.add(dimension);
    return { ...row, fix };
  });

  const checks = [...rows, ...kept];
  const count = (status: QualityStatus) => checks.filter((c) => c.status === status).length;
  return {
    checks,
    passCount: count('pass'),
    warnCount: count('warn'),
    failCount: count('fail'),
  };
}

export interface RailLine {
  key: string;
  message: string;
  blocked: boolean;
}

export function railLinesFor(
  reconciled: Pick<ReconciledVerdict, 'checks'> | null,
  issues: readonly PostingReadinessIssue[],
): RailLine[] {
  if (reconciled) {
    return reconciled.checks
      .filter((check) => check.status === 'fail' || check.status === 'warn')
      .map((check) => ({
        key: check.id,
        message: check.detail,
        blocked: check.status === 'fail',
      }));
  }
  return issues.map((issue) => ({
    key: `${issue.code}-${issue.message}`,
    message: issue.message,
    blocked: issue.severity === 'blocked',
  }));
}
